import {
  useNavigation,
  NavigationProp,
  useFocusEffect,
} from "@react-navigation/native";
import {
  OrderStatus,
  PaymentType,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import {
  GET_EVENT_OCCURRENCES,
  GET_EVENT_OCCURRENCE,
} from "@ticketingplatform/api/dist/event";
import { TICKET_LIST_QUERY } from "@ticketingplatform/api/dist/ticket";
import { usePaymentTerminalContext } from "contexts/PaymentTerminalContext";
import useEvictQueries from "hooks/useEvictQueries";
import { EventNavigationParamList } from "navigation/EventNavigation/types";
import { SCREENS } from "navigation/consts";
import { useOrderContext } from "providers/OrderProvider/hooks";
import { useCallback } from "react";
import useIntlApolloError from "hooks/useIntlApolloError";
import { checkoutErrorMessages } from "views/Checkout/consts";

export const usePayment = () => {
  const evictQueries = useEvictQueries();
  const { paymentTerminal } = usePaymentTerminalContext();
  const {
    order,
    loading,
    error: apolloError,
    setOrder,
    removeOrder,
  } = useOrderContext();
  const { navigate } =
    useNavigation<NavigationProp<EventNavigationParamList>>();

  const pay = useCallback(async () => {
    if (!order) {
      return;
    }

    const newOrder = await setOrder({
      status: OrderStatus.CHECKOUT,
      paymentTerminalId:
        order.paymentType === PaymentType.STRIPE_TERMINAL
          ? paymentTerminal?.id
          : null,
    });

    if (newOrder?.status === OrderStatus.COMPLETED) {
      evictQueries([
        GET_EVENT_OCCURRENCES,
        GET_EVENT_OCCURRENCE,
        TICKET_LIST_QUERY,
      ]);
    }

    navigate(SCREENS.OrderDetails, {
      orderId: order.id,
      pool: newOrder?.status !== OrderStatus.COMPLETED,
    });

    removeOrder();
  }, [
    order,
    setOrder,
    paymentTerminal?.id,
    removeOrder,
    navigate,
    evictQueries,
  ]);

  const error = useIntlApolloError(checkoutErrorMessages, apolloError);

  useFocusEffect(
    useCallback(() => {
      if (!order && !loading) {
        navigate(SCREENS.Cart);
      }
    }, [loading, navigate, order])
  );

  return {
    loading,
    total: order?.totalAmount || 0,
    paymentType: order?.paymentType,
    error: error?.message,
    pay,
  };
};
